import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAccounts, selectAccountsLoading, selectLoanError } from '../../../accounts/store/accounts.selector';
import { loadAccounts } from '../../../accounts/store/accounts.action';
import { catchError, filter, map, mergeMap, publishReplay, refCount, take, tap } from 'rxjs/operators';
import { SubmitStatus } from '../../../../models/form.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { customerAttributesActions } from '../../../customer-attributes/store/customer-attributes.actions';
import { combineLatest, Subject, throwError } from 'rxjs';
import { combinedIsLoading, waitForSourceData } from '../../../../utils/rxjs-util';
import { MatDialog } from '@angular/material/dialog';
import { UpdateEmailModalComponent } from '../update-email-modal/update-email-modal.component';
import { CookieService } from 'ngx-cookie-service';
import { AccountsService } from '../../../accounts/services/accounts.service';
import { getPaperlessActions, getProfileEmailActions } from '../../../profile/store/actions/profile.actions';
import {
  selectProfileEmail,
  selectProfileEmailError,
  selectProfileEmailIsLoading,
  selectProfilePaperlessIsLoaded,
} from '../../../profile/store/selectors/profile.selectors';
import { ProfileService } from '../../../profile/services/profile.service';
import { OptInService } from '../../../../services/opt-in.service';
import { getOptInActions } from '../../../opt-in/store/opt-in.actions';
import { selectOptIn, selectOptInError, selectOptInIsLoading } from '../../../opt-in/store/opt-in.selectors';
import { selectIsPaperlessPageEligible } from '../../../../store/selectors/shared-account-opt-in.selector';
import { OptInUpdateReq } from '../../../opt-in/models/opt-in.model';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { FormTracking } from '../../../../config/eev-form-tracking.config';
import { AuthEventType } from '../../../../enums/event/event.enum';
import { FieldError, FormSubmit } from '../../../../models/event-data.model';
import { AuthFormFieldName, AuthFormName, AuthFormType } from '../../../../enums/event/event-data.enum';
import { ToastService } from '../../../shared/services/toast.service';
import { BechAccount } from '../../../accounts/models/accounts.model';

interface PaperlessForm {
  isEnrolled: FormControl<boolean>;
}

@Component({
    selector: 'app-paperless-enrollment',
    templateUrl: './paperless-enrollment.component.html',
    styleUrls: ['./paperless-enrollment.component.scss'],
    standalone: false
})
export class PaperlessEnrollmentComponent implements OnInit {
  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
  isLoading$ = combinedIsLoading([
    this.store.select(selectAccountsLoading),
    this.store.select(selectProfileEmailIsLoading),
    this.store.select(selectOptInIsLoading),
  ]);
  hasError$ = combineLatest([
    this.store.select(selectLoanError),
    this.store.select(selectProfileEmailError),
    this.store.select(selectOptInError),
  ]).pipe(
    filter(([loanError, emailError, optInError]) => loanError || emailError || optInError),
    tap(() => {
      this.navigateAway();
    })
  );
  accounts$ = this.store.select(selectAccounts).pipe(publishReplay(1), refCount());
  isPaperlessPageEligible$ = waitForSourceData(
    [this.store.select(selectOptIn), this.store.select(selectProfilePaperlessIsLoaded)],
    this.store.select(selectIsPaperlessPageEligible)
  );
  profileEmail$ = this.store.select(selectProfileEmail).pipe(
    map((res) => res.email),
    tap((email) => {
      this.profileEmail = email;
    })
  );
  cid = this.cookieService.get('cid');

  isBefhCallbackDomain = this.router.getCurrentNavigation()?.extras?.state?.isBefhCallbackDomain;
  profileEmail: string;
  customerId: number;
  befhAccount: BechAccount;
  formSubmitStatus: SubmitStatus;
  submitError: string;
  paperlessForm = new FormGroup<PaperlessForm>({
    isEnrolled: new FormControl(null, [Validators.required]),
  });

  constructor(
    private readonly store: Store,
    private readonly optInService: OptInService,
    private readonly profileService: ProfileService,
    private readonly accountsService: AccountsService,
    private readonly cookieService: CookieService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly eventTrackingService: EventTrackingService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(loadAccounts());
    this.store.dispatch(customerAttributesActions.start({ request: {} }));
    this.store.dispatch(getOptInActions.start({ request: null }));

    this.isPaperlessPageEligible$.pipe(take(1)).subscribe((isPaperlessPageEligible) => {
      if (isPaperlessPageEligible) {
        this.eventTrackingService.trackPageViewEvent({
          eventName: 'pageView',
          siteSection: 'hub',
        });
      } else {
        this.navigateAway();
      }
    });
    this.accounts$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe((accounts) => {
        const customerId = new URL(accounts.loanAccounts[0].url).searchParams.get('domain');
        this.customerId = customerId && parseInt(customerId, 10);
        this.befhAccount = accounts.bechAccounts[0];
        this.store.dispatch(getProfileEmailActions.start({ request: { customerId: this.customerId } }));
        this.store.dispatch(getPaperlessActions.start({ request: this.customerId }));
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  openEmailModal() {
    this.matDialog.open(UpdateEmailModalComponent, {
      data: { email: this.profileEmail, customerId: this.customerId, cid: this.cid },
      maxWidth: '529px',
      panelClass: 'be-dialog',
    });
  }

  updatePaperlessStatus() {
    this.paperlessForm.markAllAsTouched();
    if (this.paperlessForm.invalid) {
      return;
    }

    // redirect if not updating; no need to submit form
    if (!this.paperlessForm.controls.isEnrolled.value) {
      return this.markPaperlessEnrollmentPageAsSeen(true);
    }

    this.formSubmitStatus = 'pending';
    this.submitError = null;
    this.eventTrackingService.trackEvent<FormSubmit>(
      AuthEventType.FORM_SUBMIT,
      FormTracking[AuthFormName.PAPERLESS_ENROLLMENT].formSubmitEvent
    );

    this.profileService
      .updatePaperlessEnrollmentStatus({
        customerId: this.customerId,
        reqBody: {
          enroll: this.paperlessForm.controls.isEnrolled.value,
        },
      })
      .pipe(
        catchError((error) => {
          this.submitError = `We were unable to enroll you in paperless statements. Please try again later.`;
          this.eventTrackingService.trackEvent<FieldError>(AuthEventType.FIELD_ERROR, {
            authFormName: AuthFormName.PAPERLESS_ENROLLMENT,
            authFormType: AuthFormType.PAPERLESS_ENROLLMENT_INTERSTITIAL,
            authFormFieldName: AuthFormFieldName.PAPERLESS_OPT_IN,
            authFormFieldErrorMessage: 'We were unable to enroll you in paperless statements. Please try again later.',
          });
          return throwError(() => error);
        }),
        mergeMap(() =>
          this.optInService.updateOptIn(this.getUpdateOptInReq(true)).pipe(
            catchError((error) => {
              this.submitError = `We were unable to save your preference. Please try again later.`;
              return throwError(() => error);
            })
          )
        ),
        take(1)
      )
      .subscribe({
        next: () => {
          this.formSubmitStatus = 'success';
          this.toastService.addToast({
            notificationType: 'success',
            text: 'Your preference was saved successfully.',
            horizontalPosition: 'center',
          });

          this.navigateAway();
        },
        error: () => (this.formSubmitStatus = 'error'),
      });
  }

  markPaperlessEnrollmentPageAsSeen(isIgnoringPaperlessBanner = false) {
    if (isIgnoringPaperlessBanner) {
      this.eventTrackingService.trackEvent<FormSubmit>(
        AuthEventType.FORM_SUBMIT,
        FormTracking[AuthFormName.PAPERLESS_DECLINE].formSubmitEvent
      );
    } else {
      this.eventTrackingService.trackEvent<FormSubmit>(
        AuthEventType.FORM_SUBMIT,
        FormTracking[AuthFormName.PAPERLESS_MAYBE_LATER].formSubmitEvent
      );
    }
    this.optInService.updateOptIn(this.getUpdateOptInReq(isIgnoringPaperlessBanner)).pipe(take(1)).subscribe();
    this.navigateAway();
  }

  navigateAway() {
    if (this.isBefhCallbackDomain) {
      this.accountsService.navigateToBefhCallbackDomain(this.befhAccount, this.cid);
    } else {
      this.router.navigate(['/customer-hub']);
    }
  }

  getUpdateOptInReq(isIgnoringPaperlessBanner: boolean): OptInUpdateReq {
    return isIgnoringPaperlessBanner
      ? [
          { featureType: 'PAPERLESS_INTERSTITIAL', optedIn: true },
          { featureType: 'PAPERLESS_BANNER', optedIn: true },
        ]
      : [{ featureType: 'PAPERLESS_INTERSTITIAL', optedIn: true }];
  }
}
